// src/pages/Profile.jsx
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProfileForm from '../components/ProfileForm'; // Import the form component
import ContactForm from '../components/ContactForm'; // Import the form component
import MedHistory from '../components/Medhistory';
import Consent from '../components/Consent';
import ResetPassword from '../components/ResetPassword';
import PasskeySettings from '../components/PasskeySettings';
import useModalA11y from '../hooks/useModalA11y';

const PROFILE_TABS = ['profile', 'contact', 'history', 'consent', 'password'];
const PROFILE_TAB_LABELS = {
  profile: 'Profile',
  contact: 'Contact Info',
  history: 'Health History',
  consent: 'Consent',
  password: 'Password',
};

// Assuming 'user' prop holds logged-in user info including userid, or we get it from localStorage
function Profile({ onClose }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(PROFILE_TABS.includes(requestedTab) ? requestedTab : 'profile');
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([PROFILE_TABS.includes(requestedTab) ? requestedTab : 'profile']));
  const [dirtyTabs, setDirtyTabs] = useState(() => new Set());
  const [unsavedNotice, setUnsavedNotice] = useState('');
  const dialogRef = useRef(null);

  const showTab = useCallback((tab) => {
    setActiveTab(tab);
    setUnsavedNotice('');
    setVisitedTabs((previousTabs) => {
      if (previousTabs.has(tab)) {
        return previousTabs;
      }

      const nextTabs = new Set(previousTabs);
      nextTabs.add(tab);
      return nextTabs;
    });
  }, []);

  const markTabDirty = useCallback((tab) => {
    setDirtyTabs((previousTabs) => {
      if (previousTabs.has(tab)) {
        return previousTabs;
      }

      const nextTabs = new Set(previousTabs);
      nextTabs.add(tab);
      return nextTabs;
    });
  }, []);

  const switchToUnsavedTab = useCallback((tab) => {
    setActiveTab(tab);
    setUnsavedNotice(`${PROFILE_TAB_LABELS[tab]} has unsaved changes. Review and save this tab before saving another profile section.`);
  }, []);

  const handleSubmitCapture = useCallback((event, submittingTab) => {
    const firstDirtyTab = PROFILE_TABS.find((tab) => dirtyTabs.has(tab));
    if (firstDirtyTab && firstDirtyTab !== submittingTab) {
      event.preventDefault();
      event.stopPropagation();
      switchToUnsavedTab(firstDirtyTab);
    }
  }, [dirtyTabs, switchToUnsavedTab]);

  const handleSaveSuccess = useCallback((savedTab) => {
    setDirtyTabs((previousTabs) => {
      const nextDirtyTabs = new Set(previousTabs);
      nextDirtyTabs.delete(savedTab);

      const nextUnsavedTab = PROFILE_TABS.find((tab) => nextDirtyTabs.has(tab));
      if (nextUnsavedTab) {
        switchToUnsavedTab(nextUnsavedTab);
      } else {
        setUnsavedNotice('');
      }

      return nextDirtyTabs;
    });
  }, [switchToUnsavedTab]);

  const getTabButtonClass = (tab) => `px-4 py-2 font-semibold text-sm border-b-2 whitespace-nowrap ${
    activeTab === tab
      ? 'border-primary text-primary'
      : 'border-transparent text-neutral-dark hover:text-primary'
  } ${dirtyTabs.has(tab) ? 'pr-2' : ''}`;

  const renderTabLabel = (tab) => (
    <>
      {PROFILE_TAB_LABELS[tab]}
      {dirtyTabs.has(tab) && (
        <span className="ml-2 inline-flex h-2.5 w-2.5 rounded-full bg-red-500" aria-hidden="true" />
      )}
    </>
  );

  useEffect(() => {
    if (PROFILE_TABS.includes(requestedTab)) {
      showTab(requestedTab);
    }
  }, [requestedTab, showTab]);
  
  const handleOnClose = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
      return;
    }
    navigate('/dashboard');
  }, [navigate, onClose]);

  useModalA11y({
    isOpen: true,
    dialogRef,
    onClose: handleOnClose,
    lockBodyScroll: true,
  });

  // Render logic
  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profileModalTitle"
        tabIndex={-1}
        className="w-full max-w-4xl h-screen md:h-[80vh] bg-white sm:rounded-lg shadow-lg flex flex-col overflow-y-auto"
      >

        {/* Header */}
        <div className="p-3 border-b flex items-center justify-between shrink-0">
          <h2 id="profileModalTitle" className="text-2xl font-bold text-primary">My Profile</h2>
          <button
            type="button"
            onClick={handleOnClose}
            aria-label="Close profile"
            className="text-dark text-2xl hover:text-button-dark" // Assuming text-dark and text-button-dark are defined in your Tailwind config
          >
            &times;
          </button>
        </div>

        {/* Tabs */}
        <div className="px-4 pt-2 border-b shrink-0 overflow-x-auto">
          {/* This single div should contain all tab buttons */}
          <div className="flex flex-nowrap space-x-2 min-w-max" role="tablist" aria-label="Profile sections">
            <button
              type="button"
              role="tab"
              id="profile-tab-profile"
              aria-selected={activeTab === 'profile'}
              aria-controls="profile-panel-profile"
              onClick={() => showTab('profile')}
              className={getTabButtonClass('profile')}
            >
              {renderTabLabel('profile')}
            </button>
            {/* Contact and History buttons are siblings to the Profile button */}
            <button
              type="button"
              role="tab"
              id="profile-tab-contact"
              aria-selected={activeTab === 'contact'}
              aria-controls="profile-panel-contact"
              onClick={() => showTab('contact')}
              className={getTabButtonClass('contact')}
            >
              {renderTabLabel('contact')}
            </button>
            <button
              type="button"
              role="tab"
              id="profile-tab-history"
              aria-selected={activeTab === 'history'}
              aria-controls="profile-panel-history"
              onClick={() => showTab('history')}
              className={getTabButtonClass('history')}
            >
              {renderTabLabel('history')}
            </button>
            <button
              type="button"
              role="tab"
              id="profile-tab-consent"
              aria-selected={activeTab === 'consent'}
              aria-controls="profile-panel-consent"
              onClick={() => showTab('consent')}
              className={getTabButtonClass('consent')}
            >
              {renderTabLabel('consent')}
            </button>
            <button
              type="button"
              role="tab"
              id="profile-tab-password"
              aria-selected={activeTab === 'password'}
              aria-controls="profile-panel-password"
              onClick={() => showTab('password')}
              className={getTabButtonClass('password')}
            >
              {renderTabLabel('password')}
            </button>
          </div> {/* Closing tag for the flex container holding all buttons */}
        </div> {/* Closing tag for the main tabs container */}

        {unsavedNotice && (
          <div className="mx-4 mt-3 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert">
            {unsavedNotice}
          </div>
        )}

        {/* Scrollable Content */}
        <div className="flex-grow px-4 pb-6 min-h-0 overflow-y-auto">
          {visitedTabs.has('profile') && (
            <section
              id="profile-panel-profile"
              role="tabpanel"
              aria-labelledby="profile-tab-profile"
              hidden={activeTab !== 'profile'}
              onInputCapture={() => markTabDirty('profile')}
              onChangeCapture={() => markTabDirty('profile')}
              onSubmitCapture={(event) => handleSubmitCapture(event, 'profile')}
            >
              <ProfileForm
                onSaveComplete={handleOnClose} // Call onClose when standalone save is done
                onSaveSuccess={() => handleSaveSuccess('profile')}
                onDirty={() => markTabDirty('profile')}
                isWorkflowStep={false} // Explicitly false or omitted
              />
            </section>
          )}

          {visitedTabs.has('contact') && (
            <section
              id="profile-panel-contact"
              role="tabpanel"
              aria-labelledby="profile-tab-contact"
              hidden={activeTab !== 'contact'}
              onInputCapture={() => markTabDirty('contact')}
              onChangeCapture={() => markTabDirty('contact')}
              onSubmitCapture={(event) => handleSubmitCapture(event, 'contact')}
            >
              <ContactForm
                onSaveComplete={handleOnClose} // Call onClose when standalone save is done
                onSaveSuccess={() => handleSaveSuccess('contact')}
                onDirty={() => markTabDirty('contact')}
                isWorkflowStep={false} // Explicitly false or omitted
              />
            </section>
          )}

          {visitedTabs.has('history') && (
            <section
              id="profile-panel-history"
              role="tabpanel"
              aria-labelledby="profile-tab-history"
              hidden={activeTab !== 'history'}
              onInputCapture={() => markTabDirty('history')}
              onChangeCapture={() => markTabDirty('history')}
              onSubmitCapture={(event) => handleSubmitCapture(event, 'history')}
            >
              <MedHistory
                isWorkflowStep={false}
                onSaveComplete={handleOnClose} // Or close modal
                onSaveSuccess={() => handleSaveSuccess('history')}
                onDirty={() => markTabDirty('history')}
              />
            </section>
          )}

          {visitedTabs.has('consent') && (
            <section
              id="profile-panel-consent"
              role="tabpanel"
              aria-labelledby="profile-tab-consent"
              hidden={activeTab !== 'consent'}
              onInputCapture={() => markTabDirty('consent')}
              onChangeCapture={() => markTabDirty('consent')}
              onSubmitCapture={(event) => handleSubmitCapture(event, 'consent')}
            >
              <Consent
                isWorkflowStep={false}
                onSaveComplete={handleOnClose} // Or close modal
                onSaveSuccess={() => handleSaveSuccess('consent')}
                onDirty={() => markTabDirty('consent')}
              />
            </section>
          )}

          {visitedTabs.has('password') && (
            <section
              id="profile-panel-password"
              role="tabpanel"
              aria-labelledby="profile-tab-password"
              hidden={activeTab !== 'password'}
              onInputCapture={() => markTabDirty('password')}
              onChangeCapture={() => markTabDirty('password')}
              onSubmitCapture={(event) => handleSubmitCapture(event, 'password')}
            >
              <ResetPassword
                isWorkflowStep={false}
                onSaveComplete={handleOnClose} // Or close modal
                onSaveSuccess={() => handleSaveSuccess('password')}
                onDirty={() => markTabDirty('password')}
              />
              <PasskeySettings />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
